import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { useCrmInstruments } from '@/api/instruments/use-instruments';
import { useTradingAccounts } from '@/api/trading-accounts/use-crm-trading-accounts';
import { OrderFormContent } from '@/pages/portal/trade/components/OrderFormContent';
import { buildInstrumentRealTimePrice, resolveInstrumentType } from '@/utils/instrumentHelpers';
import { getDefaultLeverage } from '@/utils/leverageOptions';
import { useDebounce } from '@/hooks/useDebounce';

interface CreateTradeModalProps {
    isOpen: boolean;
    onClose: () => void;
    customerId: string;
    onSuccess?: () => void;
}

const CreateTradeModal: React.FC<CreateTradeModalProps> = ({
    isOpen,
    onClose,
    customerId,
    onSuccess,
}) => {
    const { t } = useTranslation('trading');
    const [direction, setDirection] = useState<'buy' | 'sell'>('buy');
    const [selectedInstrumentId, setSelectedInstrumentId] = useState('');
    const [instrumentSearch, setInstrumentSearch] = useState('');
    const debouncedInstrumentSearch = useDebounce(instrumentSearch, 300);

    const { data: instrumentsData, isLoading: instrumentsLoading } = useCrmInstruments({
        per_page: 50,
        search: debouncedInstrumentSearch || undefined,
    });
    const {
        data: accountsData,
        isLoading: accountsLoading,
        refetch: refetchAccounts,
    } = useTradingAccounts({
        customer_id: customerId,
        per_page: 100,
    });

    const instruments = instrumentsData?.data || [];
    const accounts = accountsData?.data || [];
    const selectedInstrument = instruments.find(inst => inst.id === selectedInstrumentId);
    const instrumentType = resolveInstrumentType(selectedInstrument);
    const realTimePrice = buildInstrumentRealTimePrice(selectedInstrument);
    const selectedPair = selectedInstrument?.symbol || t('selectInstrument', 'Instrument');

    const instrumentData = selectedInstrument
        ? {
              min_lot: Number(selectedInstrument.min_lot) || 0.01,
              max_lot: Number(selectedInstrument.max_lot) || 100,
              max_leverage:
                  Number(selectedInstrument.max_leverage) ||
                  Number(selectedInstrument.leverage) ||
                  getDefaultLeverage(instrumentType),
              leverage: Number(selectedInstrument.leverage) || getDefaultLeverage(instrumentType),
          }
        : undefined;

    useEffect(() => {
        if (isOpen) {
            setDirection('buy');
            setSelectedInstrumentId('');
            setInstrumentSearch('');
            refetchAccounts();
        }
    }, [isOpen, refetchAccounts]);

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            onClose();
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-xl font-semibold">Create New Trade</DialogTitle>
                </DialogHeader>

                <div className="grid grid-cols-2 gap-3">
                    <Button
                        type="button"
                        onClick={() => setDirection('buy')}
                        variant={direction === 'buy' ? 'success' : 'outline'}
                        size="lg"
                        className={`h-12 font-semibold transition-all duration-200 hover:scale-105 ${
                            direction === 'buy'
                                ? ''
                                : 'bg-success/10 hover:bg-success/20 text-success border-success/30'
                        }`}
                    >
                        <TrendingUp className="h-5 w-5" />
                        {t('buy')}
                    </Button>
                    <Button
                        type="button"
                        onClick={() => setDirection('sell')}
                        variant={direction === 'sell' ? 'destructive' : 'outline'}
                        size="lg"
                        className={`h-12 font-semibold transition-all duration-200 hover:scale-105 ${
                            direction === 'sell'
                                ? ''
                                : 'bg-destructive/10 hover:bg-destructive/20 text-destructive border-destructive/30'
                        }`}
                    >
                        <TrendingDown className="h-5 w-5" />
                        {t('sell')}
                    </Button>
                </div>

                {accountsLoading ? (
                    <div className="py-8 text-center text-sm text-muted-foreground">
                        Loading trading accounts...
                    </div>
                ) : accounts.length === 0 ? (
                    <div className="py-8 text-center text-sm text-muted-foreground">
                        No trading accounts found for this customer.
                    </div>
                ) : (
                    <OrderFormContent
                        key={isOpen ? `crm-trade-${customerId}` : 'closed'}
                        variant="crm"
                        customerId={customerId}
                        direction={direction}
                        selectedPair={selectedPair}
                        selectedInstrumentId={selectedInstrumentId || undefined}
                        instruments={instruments}
                        instrumentsLoading={instrumentsLoading}
                        onInstrumentChange={setSelectedInstrumentId}
                        onInstrumentSearch={setInstrumentSearch}
                        allowInstrumentPriceFallback
                        realTimePrice={realTimePrice}
                        tradingAccounts={accounts}
                        instrumentType={instrumentType}
                        instrumentData={instrumentData}
                        isPricesLoading={
                            instrumentsLoading && !realTimePrice && !!selectedInstrumentId
                        }
                        onClose={onClose}
                        onOrderSuccess={onSuccess}
                        submitLabel="Create Trade"
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};

export default CreateTradeModal;
